import {Box, Button, Container, Header, Link, SpaceBetween, StatusIndicator} from '@amzn/awsui-components-react';
import React, { useMemo } from 'react';
import {useHistory} from 'react-router-dom';
import {TPELoadingSpinner} from '../shared/TPELoadingSpinner';
import {getPermissions} from '../AppPermissions';
import {AppModules} from 'src/models/permissions/RolePermissions';
import TPPolarisTable from '../shared/TPPolarisTable';
import {TableProps} from '@amzn/awsui-components-react/polaris/table';
import {
    DEFAULT_COLLECTION_PREFERENCES, FILTERING_PROPERTIES, PAGE_SIZE_OPTIONS,
    VISIBLE_CONTENT_OPTIONS, COLUMN_DEFINITIONS, COLUMN_IDS
} from "src/components/multidimensional-allocation/common/Constants";
import CreateWorksheetModal from './CreateWorksheetModal';
import {useAllocationData} from './hooks/useAllocationData';
import {useWorksheetCreation} from './hooks/useWorksheetCreation';

/**
 * Component for displaying and managing multidimensional allocation calculations
 * Provides a table view of allocation worksheets with create, view, and filter capabilities
 */
export default function AllocationCalculations() {
    const {canEdit} = getPermissions(AppModules.MULTIDIMENSIONAL_ALLOCATION);
    const history = useHistory();

    // Use custom hooks for data management and worksheet creation
    const {isLoading, error, worksheets, itemCount, services} = useAllocationData();
    const {
        isModalVisible,
        createLoading,
        createError,
        handleCreateWorksheetClick,
        handleModalDismiss,
        handleModalSubmit
    } = useWorksheetCreation(services);

    // Column definitions with inline navigation logic - simpler and more direct
    const columnDefinitions = useMemo((): TableProps.ColumnDefinition<any>[] => [
        {
            ...COLUMN_DEFINITIONS.WORKSHEET_NAME,
            cell: (item: any) => (
                <Link
                    href="#"
                    onFollow={(event) => {
                        event.preventDefault();
                        history.push(`/calculations/multidimensionalAllocation/calculation/${item.worksheetId}`);
                    }}
                >
                    {item.worksheetName}
                </Link>
            ),
            isRowHeader: true,
        },
        {
            ...COLUMN_DEFINITIONS.STATUS,
            cell: (item: any) => (
                <StatusIndicator
                    type={item.status === 'ACTIVE' ? 'success' : 'in-progress'}
                >
                    {item.status}
                </StatusIndicator>
            ),
        },
        {
            ...COLUMN_DEFINITIONS.CREATED_BY,
            cell: (item: any) => item.createdBy || '-',
        },
        {
            ...COLUMN_DEFINITIONS.CREATED_ON,
            cell: (item: any) => item.createdOn || '-',
        },
        {
            ...COLUMN_DEFINITIONS.LAST_UPDATED_BY,
            cell: (item: any) => item.lastUpdatedBy || '-',
        },
        {
            ...COLUMN_DEFINITIONS.LAST_UPDATED_ON,
            cell: (item: any) => item.lastUpdatedOn || '-',
        },
    ], [history]);

    // Show loading state with TPELoadingSpinner following existing patterns
    return (
        <Container
            header={
                <Header
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button
                                variant="primary"
                                data-class="createWorksheetButton"
                                onClick={handleCreateWorksheetClick}
                                disabled={!canEdit}
                            >
                                Create Worksheet
                            </Button>
                        </SpaceBetween>
                    }
                >
                    Worksheets ({itemCount})
                </Header>
            }
        >
            <TPELoadingSpinner loading={isLoading} loadingText="Loading allocation calculations">
                {error ? (
                    <Box textAlign="center" padding="l">
                        <span className="errorMessage">Error loading allocation calculations: {error}</span>
                    </Box>
                ) : (
                    <TPPolarisTable
                        sortingColumn="worksheetName"
                        tableEmptyState={
                            <Box textAlign="center" padding="l">
                                <span className="noDataMessage">No allocation calculations available</span>
                            </Box>
                        }
                        columnDefinitions={columnDefinitions}
                        tableItems={worksheets}
                        defaultPreferences={DEFAULT_COLLECTION_PREFERENCES}
                        preferencesStorageKey="allocationCalculationsPreferencesStorageKey"
                        visibleContentOptions={VISIBLE_CONTENT_OPTIONS}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        filteringEmptyText="There are no allocation calculations for the selected filter conditions."
                        filteringLoadingText="Loading allocation calculations for the filter conditions applied."
                        filteringProperties={FILTERING_PROPERTIES}
                        loadingText="Loading allocation calculations."
                        resizableColumns={true}
                        visibleColumns={COLUMN_IDS}
                        isLoading={isLoading}
                    />
                )}
            </TPELoadingSpinner>

            {/* Create Worksheet Modal */}
            <CreateWorksheetModal
                visible={isModalVisible}
                onClose={handleModalDismiss}
                onSubmit={handleModalSubmit}
                isCreating={createLoading}
                createError={createError}
            />
        </Container>
    );
}
